import { isNativeApp } from "@/lib/nativeBridge";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { StorefrontIcon as Storefront, MagnifyingGlassIcon as MagnifyingGlass, MapPinIcon as MapPin, PackageIcon as Package } from "@phosphor-icons/react";
import api from "@/lib/api";
import { thumbUrl } from "@/lib/utils-app";
import AppShell from "@/components/AppShell";
import { Input } from "@/components/ui/input";

/**
 * Public directory of kennel storefronts at `/storefronts`.
 *
 * Two-axis discovery: free-text search by store name + optional ZIP
 * filter that respects each store's declared local-delivery zone.
 * Stores with zero in-stock products are hidden server-side so the
 * grid never shows empty shelves.
 *
 * Wired into the Explore menu so the directory is a one-tap hop from
 * the marketplace, breeders, and feed surfaces — the same place
 * shoppers already browse the registry.
 */
export default function PublicStorefronts() {
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [zip, setZip] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const params = new URLSearchParams();
    if (q.trim()) params.set("q", q.trim());
    if (zip.trim().length === 5) params.set("zip", zip.trim());
    api.get(`/store?${params.toString()}`)
      .then(({ data }) => { if (!cancelled) setStores(data.stores || []); })
      .catch(() => { if (!cancelled) setStores([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [q, zip]);

  return (
    <AppShell>
      <Helmet>
        <title>The Dog Shop — Armstrong K9 Registry</title>
        <meta name="description" content="Browse The Dog Shop — independent storefronts selling dog food, merch, and gear directly by the breeders who use them." />
      </Helmet>

      <div className="max-w-6xl mx-auto px-6 py-10" data-testid="storefronts-directory">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-gold">
          <Storefront size={14} weight="duotone" /> Directory
        </div>
        <h1 className="font-spectral text-4xl sm:text-5xl text-ink mt-3">The Dog Shop</h1>
        <p className="text-[#3a3a3a] mt-2 max-w-2xl leading-relaxed">
          Independent storefronts run by the kennels themselves — dog food,
          treats, supplements, and merch sold directly by the breeders who use them.
          Most stores deliver locally; some ship merch nationwide.
        </p>

        {/* Search + ZIP filter */}
        <div className="mt-6 flex flex-col sm:flex-row gap-3 max-w-2xl">
          <div className="flex-1 relative">
            <MagnifyingGlass size={16} weight="duotone" className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9A9A9A]" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search storefronts by name"
              className="pl-9 bg-white"
              data-testid="storefronts-search"
            />
          </div>
          <div className="relative w-full sm:w-44">
            <MapPin size={14} weight="duotone" className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9A9A9A]" />
            <Input
              value={zip}
              onChange={(e) => setZip(e.target.value.replace(/[^0-9]/g, "").slice(0, 5))}
              placeholder="ZIP code"
              inputMode="numeric"
              maxLength={5}
              className="pl-9 bg-white font-tabular"
              data-testid="storefronts-zip"
            />
          </div>
        </div>
        {zip.length === 5 && (
          <p className="text-[12px] text-[#6B6B6B] mt-2">
            Showing stores that deliver to <span className="font-tabular text-ink">{zip}</span> + any storefronts that ship anywhere.
          </p>
        )}

        {/* Grid */}
        <div className="mt-8">
          {loading ? (
            <p className="text-[#6B6B6B] text-sm">Loading storefronts…</p>
          ) : stores.length === 0 ? (
            <EmptyState hasFilters={!!(q || zip)} />
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="storefronts-grid">
              {stores.map((s) => <StoreCard key={s.id} store={s} />)}
            </div>
          )}
        </div>

        {/* Open-your-own CTA — hidden natively (subscription price, 3.1.1) */}
        {!isNativeApp() && (
        <div className="mt-12 rounded-2xl border border-[#E8D9A6] bg-[#FBF7EC]/60 p-6 flex items-center gap-4 flex-wrap">
          <Storefront size={28} weight="duotone" className="text-gold-dark" />
          <div className="flex-1 min-w-[200px]">
            <h2 className="font-spectral text-xl text-ink">Want your own storefront here?</h2>
            <p className="text-[12.5px] text-[#6B6B6B] mt-0.5">
              Subscribe to The Dog Shop ($19/mo) and your page goes live in this directory the moment you add a product.
            </p>
          </div>
          <Link
            to="/billing"
            className="rounded-full bg-ink hover:bg-crimson text-white px-5 py-2.5 text-[13px] font-semibold transition-colors"
            data-testid="open-store-cta"
          >
            Open your storefront →
          </Link>
        </div>
        )}

        <p className="mt-8 text-[11px] text-[#9A9A9A] leading-relaxed border-t border-[#EEE] pt-4">
          Each storefront listed here is operated by an independent kennel. Armstrong K9 Registry
          is the technology and payments facilitator — not the seller of their products. See the{" "}
          <Link to="/terms" className="underline">Terms of Service §5b</Link> and the{" "}
          <Link to="/storefront-seller-agreement" className="underline">Storefront Seller Agreement</Link>{" "}
          for details.
        </p>
      </div>
    </AppShell>
  );
}

function StoreCard({ store }) {
  const hero = store.hero_image ? (thumbUrl(store.hero_image) || store.hero_image) : null;
  return (
    <Link
      to={`/store/${store.slug}`}
      className="block rounded-2xl border border-[#E8E0CC] bg-white overflow-hidden hover:shadow-[0_18px_44px_rgba(14,14,14,0.12)] hover:border-gold-dark transition-all group"
      data-testid={`store-card-${store.slug}`}
    >
      <div className="aspect-[16/9] bg-gradient-to-br from-[#FBF7EC] to-[#E8D9A6]/40 flex items-center justify-center overflow-hidden relative">
        {hero ? (
          <img src={hero} alt={store.name} className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform" />
        ) : (
          <Storefront size={42} weight="duotone" className="text-gold/60" />
        )}
        {store.is_featured && (
          <span className="absolute top-2 left-2 text-[10px] font-bold uppercase tracking-[0.15em] rounded-full px-2 py-0.5 bg-gold-gradient text-ink shadow">
            ★ Featured
          </span>
        )}
      </div>
      <div className="p-4">
        <h3 className="font-spectral text-lg leading-tight text-ink">{store.name}</h3>
        {store.bio && (
          <p className="text-[12.5px] text-[#6B6B6B] mt-1 line-clamp-2 leading-relaxed">{store.bio}</p>
        )}
        <div className="flex items-center justify-between mt-3 text-[11px] text-[#6B6B6B]">
          <span className="flex items-center gap-1">
            <Package size={11} weight="duotone" />
            {store.product_count} product{store.product_count === 1 ? "" : "s"}
          </span>
          <span className="flex items-center gap-1">
            <MapPin size={11} weight="duotone" />
            {store.zone_enforced ? "Local delivery" : "Open to all"}
          </span>
        </div>
      </div>
    </Link>
  );
}

function EmptyState({ hasFilters }) {
  return (
    <div className="text-center py-12 border border-dashed border-[#E8E0CC] rounded-xl bg-white">
      <Storefront size={36} weight="duotone" className="text-[#9A9A9A] mx-auto" />
      <p className="font-spectral text-lg text-ink mt-3">
        {hasFilters ? "No matches for your search" : "The directory is just getting started"}
      </p>
      <p className="text-[12.5px] text-[#6B6B6B] mt-1 max-w-md mx-auto">
        {hasFilters
          ? "Try widening your search or removing the ZIP filter."
          : "Be the first kennel to open a storefront — yours will be the only listing for a while, which makes for great early SEO."}
      </p>
    </div>
  );
}
